import * as Sentry from '@sentry/nextjs';
import getenv from 'getenv';
import { once } from 'lodash';

import type { CaptureAddToCartPayload, CaptureConversionPayload, CSID, Tracify } from './tracify.contracts';
import { CSID_MARKETING, CSID_WEBSHOP, EVENT_TYPE_TO_CSIDS_MAP } from './tracify.config';

const isRunningInDevelopmentMode: boolean = getenv('NODE_ENV', process.env.NODE_ENV) === 'development';
const isRunningOnServerSide: boolean = typeof window === 'undefined';

export class TracifyService implements Tracify {
  /**
   * Determines whether the Tracify instances have been correctly initialized and are ready to use.
   *
   * @default false
   */
  private areTracifyInstancesInitialized: boolean = false;

  /**
   * A map holding the relation between the CSID and the associated Tracify's instance.
   *
   * @default {}
   */
  private csidToTracifyInstanceMap: Record<CSID, Tracify> = {};

  constructor() {
    void this.initializeTracifyInstances();
  }

  /**
   * @see Tracify.captureAddToCart
   */
  public async captureAddToCart(payload: CaptureAddToCartPayload): Promise<void> {
    if (!this.areTracifyInstancesInitialized) {
      await this.initializeTracifyInstances();
    }

    await Promise.all(
      EVENT_TYPE_TO_CSIDS_MAP.captureAddToCart.map(csid => {
        const tracifyInstance: Tracify = this.getTracifyInstanceByCsid(csid);
        return tracifyInstance.captureAddToCart(payload);
      })
    );
  }

  /**
   * @see Tracify.captureConversion
   */
  public async captureConversion(payload: CaptureConversionPayload): Promise<void> {
    if (!this.areTracifyInstancesInitialized) {
      await this.initializeTracifyInstances();
    }

    await Promise.all(
      EVENT_TYPE_TO_CSIDS_MAP.captureConversion.map(csid => {
        const tracifyInstance: Tracify = this.getTracifyInstanceByCsid(csid);
        return tracifyInstance.captureConversion(payload);
      })
    );
  }

  /**
   * @see Tracify.capturePageView
   */
  public async capturePageView(): Promise<void> {
    if (!this.areTracifyInstancesInitialized) {
      await this.initializeTracifyInstances();
    }

    await Promise.all(
      EVENT_TYPE_TO_CSIDS_MAP.capturePageView.map(csid => {
        const tracifyInstance: Tracify = this.getTracifyInstanceByCsid(csid);
        return tracifyInstance.capturePageView();
      })
    );
  }

  /**
   * Resolves the Tracify instance applicable to the provided CSID.
   *
   * @param csid - The CSID which associated Tracify instance we want to get.
   *
   * @throws Error - If there's no Tracify instance associated with the given CSID.
   */
  private getTracifyInstanceByCsid(csid: CSID): Tracify {
    const tracifyInstance: Tracify = this.csidToTracifyInstanceMap[csid];

    if (typeof tracifyInstance === 'undefined') {
      throw new Error(`There's no Tracify instance associated with the following CSID: ${csid}`);
    }

    return tracifyInstance;
  }

  /**
   * Initializes the Tracify instances.
   */
  private initializeTracifyInstances: () => Promise<void> = once(() => {
    return new Promise(resolve => {
      if (isRunningOnServerSide) {
        return resolve();
      }

      const apiScript = document.createElement('script');

      apiScript.src = `https://${
        process.env.NODE_ENV === 'development' ? 'devscripting' : 'scripting'
      }.tracify.ai/api.v1.js`;

      apiScript.async = true;

      apiScript.onload = () => {
        if (typeof window.Tracify !== 'function') {
          throw new Error('Tracify script failed to load!');
        }

        try {
          this.csidToTracifyInstanceMap = {
            [CSID_MARKETING]: window.Tracify({ csid: CSID_MARKETING, fingerPrint: true }),
            [CSID_WEBSHOP]: window.Tracify({ csid: CSID_WEBSHOP, fingerPrint: true }),
          };

          this.areTracifyInstancesInitialized = true;
        } catch (caughtError) {
          const error = new Error(`Failed to initialize the Tracify instances! ${caughtError}`);

          if (isRunningInDevelopmentMode) {
            throw error;
          } else {
            Sentry.captureException(error);
          }
        }

        return resolve();
      };

      document.body.appendChild(apiScript);
    });
  });
}

export const tracifyService = new TracifyService();
